import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Aviso } from '@/components/ui/aviso';
import { EstadoError } from '@/components/ui/estado-vacio';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad, formatoFecha, formatoNumero } from '@/lib/utils/formato-precio';
import { usePedidoConEspera } from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import type { Cadence } from '../../types/strategy-applications.types';
import { PermisoDeEstrategia, permisoEnPalabras, PERMISO_INICIAL } from '../permiso-de-estrategia';
import { ReglasQueLaAlcanzan } from '../reglas-que-la-alcanzan';
import {
  cuerpoDeSimulacion,
  grupoParaContar,
  leeLaCompetencia,
  numeroValido,
  type BorradorDeEstrategia,
} from './borrador';
import { METODOS } from './paso-2-metodo';
import { VerCualesYaTienenOtra } from './ver-cuales';

/**
 * Paso 3 · Cuándo y con qué permiso (maqueta `C-Nueva4-Revisar`): cada cuánto
 * revisa, HASTA CUÁNDO corre (son dos cosas distintas), el permiso
 * (`PermisoDeEstrategia`, plan 4B T6), las reglas que la van a limitar
 * (`ReglasQueLaAlcanzan`, con la estrategia EN BORRADOR: la estrategia no tiene
 * reglas propias), los que ya tiene otra estrategia y el resumen.
 */
export const NOMBRE_CADENCIA: Record<Cadence, string> = {
  INSTANT: 'En cuanto lea la competencia',
  DAILY: 'Todos los días',
  WEEKLY: 'Una vez por semana',
  ON_DEMAND: 'Sólo cuando yo lo pida',
};
const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
/** «los miércoles», «los sábados»: el día de la semana en plural, para el resumen. */
const LOS_DIAS = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];

// Lote B2 (D10; auditoría H4): un día de calendario elegido acá es de Bolivia;
// el mediodía de Bolivia lo deja en ese día en cualquier zona de la computadora.
const fecha = (d: string) => formatoFecha(`${d}T12:00:00-04:00`);

/**
 * 6 y 0 → «06:00» (pulido: antes se leía «6 : 0»). Es una hora de reloj, no un
 * instante: no pasa por `formatoHora` (que desde el lote B2 convierte a la hora
 * de Bolivia y en una computadora en UTC leía «06:00» como «02:00»; auditoría H4).
 */
const horaEnTexto = (hora: number, minuto: number) =>
  `${String(hora).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;

/** «25 % sobre el precio», «3 % debajo del más barato»: el número del método, en palabras. */
function numeroEnPalabras(b: BorradorDeEstrategia): string | null {
  const n = numeroValido(b.metodo, b.valor);
  if (n == null) return null;
  const pct = `${formatoNumero(n)} %`;
  if (b.metodo === 'MARGIN_TARGET') return `${pct} sobre el precio`;
  if (b.metodo === 'UNDERCUT_PERCENT') return `${pct} debajo del más barato`;
  if (b.metodo === 'DISCOUNT_PERCENT') return `${pct} menos que el precio de lista`;
  return null;
}

/** «todos los días a las 06:00», «los miércoles a las 07:30», «sólo cuando yo lo pida». */
function cadenciaEnPalabras(b: BorradorDeEstrategia): string {
  const hora = horaEnTexto(b.hora, b.minuto);
  if (b.cadencia === 'DAILY') return `todos los días a las ${hora}`;
  if (b.cadencia === 'WEEKLY') return `los ${LOS_DIAS[b.dia]} a las ${hora}`;
  return NOMBRE_CADENCIA[b.cadencia].toLowerCase();
}

/**
 * El resumen de lo elegido, en una línea (maqueta: «Apuntar a un margen · 25 %
 * sobre el precio · todos los días a las 06:00 · sin fecha de fin · Siempre te
 * pregunta»). Sin permiso válido, «—».
 */
export function resumenDelBorrador(b: BorradorDeEstrategia): string {
  return [
    METODOS.find((m) => m.metodo === b.metodo)?.titulo ?? 'Método',
    numeroEnPalabras(b),
    cadenciaEnPalabras(b),
    b.hastaCuando ? `hasta el ${fecha(b.hastaCuando)}` : 'sin fecha de fin',
    b.permiso ? permisoEnPalabras(b.permiso.mode, b.permiso.askAboveChangePct) : '—',
  ]
    .filter((x): x is string => x != null)
    .join(' · ');
}

/**
 * El resumen del subtítulo del paso 4 (maqueta agregados.html#paso4): método con su
 * número, permiso y horario, en ese orden; sin la fecha de fin. Sin permiso, «—».
 */
export function resumenParaElSubtitulo(b: BorradorDeEstrategia): string {
  return [
    METODOS.find((m) => m.metodo === b.metodo)?.titulo ?? 'Método',
    numeroEnPalabras(b),
    b.permiso ? permisoEnPalabras(b.permiso.mode, b.permiso.askAboveChangePct) : '—',
    cadenciaEnPalabras(b),
  ]
    .filter((x): x is string => x != null)
    .join(' · ');
}

export function Paso3Cuando({
  borrador: b,
  onChange,
  onSolapados,
  avisoOriginalActiva = false,
}: {
  borrador: BorradorDeEstrategia;
  onChange: (cambios: Partial<BorradorDeEstrategia>) => void;
  /** Cuántos ya tiene otra estrategia (`null` = todavía no se sabe). El diálogo no crea sin decidir. */
  onSolapados?: (n: number | null) => void;
  /** P3-D8 (D1 del 27-sep noche): la original no venció, activa o apagada, y sigue siendo dueña. */
  avisoOriginalActiva?: boolean;
}) {
  // El nombre fijo (27-sep, B5): tipear el nombre no vuelve a pedir `/preview`.
  const grupo = grupoParaContar(b);
  const solape = usePedidoConEspera(grupo ? JSON.stringify(grupo) : null, () =>
    strategyApplicationsApi.vistaPrevia(grupo!)
  );
  const solapados = solape.estado === 'LISTO' ? solape.data.solapados : null;
  useEffect(() => {
    onSolapados?.(solapados);
  }, [solapados, onSolapados]);
  const conHora = b.cadencia === 'DAILY' || b.cadencia === 'WEEKLY';
  // Escaneo previo Nº 8: con un método que no lee la competencia no se ofrece
  // «En cuanto lea la competencia» (no correría nunca), y si el borrador ya lo
  // tenía (se cambió de método, o se duplicó una vieja) vuelve a «Todos los días».
  const ofreceAlLeer = leeLaCompetencia(b.metodo);
  useEffect(() => {
    if (!ofreceAlLeer && b.cadencia === 'INSTANT') onChange({ cadencia: 'DAILY' });
  }, [ofreceAlLeer, b.cadencia, onChange]);

  // Los dos botones de los que ya tiene otra: iguales, y el elegido marcado.
  const eleccion = (activa: boolean) =>
    cn('flex-1', activa && 'border-primary bg-primary/10 text-primary hover:bg-primary/10');

  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <div data-testid="cadencia" className="space-y-1.5">
          <Label>Cada cuánto revisa</Label>
          <div className="flex flex-wrap items-end gap-2">
            <Select value={b.cadencia} onValueChange={(v) => onChange({ cadencia: v as Cadence })}>
              <SelectTrigger className="w-64" aria-label="Cada cuánto revisa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(NOMBRE_CADENCIA) as Cadence[])
                  .filter((k) => k !== 'INSTANT' || ofreceAlLeer)
                  .map((k) => (
                    <SelectItem key={k} value={k}>
                      {NOMBRE_CADENCIA[k]}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {b.cadencia === 'WEEKLY' && (
              <Select value={String(b.dia)} onValueChange={(v) => onChange({ dia: Number(v) })}>
                <SelectTrigger className="w-40" aria-label="Día">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIAS.map((d, i) => (
                    <SelectItem key={d} value={String(i)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {conHora && (
              // Un solo campo de hora (pulido): «06:00», no un «6» y un «0» sueltos.
              <div data-testid="hora-de-revision" className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">a las</span>
                <div className="w-28">
                  <Input
                    aria-label="Hora"
                    type="time"
                    className="tabular-nums dark:[color-scheme:dark]"
                    value={horaEnTexto(b.hora, b.minuto)}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      if (Number.isFinite(h) && Number.isFinite(m))
                        onChange({
                          hora: Math.min(23, Math.max(0, h)),
                          minuto: Math.min(59, Math.max(0, m)),
                        });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {!ofreceAlLeer && (
            <p data-testid="sin-al-leer" className="text-xs text-muted-foreground">
              Este método no lee la competencia: revisa todos los días, una vez por semana o cuando
              lo pidas.
            </p>
          )}
        </div>

        <div data-testid="vigencia" className="space-y-1.5">
          <Label>Hasta cuándo corre</Label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              aria-label="Tiene fecha de fin"
              checked={b.hastaCuando != null}
              onCheckedChange={(v) =>
                onChange({
                  hastaCuando: v
                    ? new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10)
                    : null,
                })
              }
            />
            Tiene fecha de fin
          </label>
          {b.hastaCuando == null ? (
            <p className="text-xs text-muted-foreground">
              Corre hasta que la apagues. «Cada cuánto revisa» no la termina: sólo dice cuándo mira.
            </p>
          ) : (
            <>
              <Input
                aria-label="Fecha de fin"
                type="date"
                className="w-48"
                value={b.hastaCuando}
                onChange={(e) => onChange({ hastaCuando: e.target.value || null })}
              />
              {/* Plan 2: P2-D2, P2-D3 y (27-sep) P2-D5 a P2-D7 (escaneo previo Nº 1). El permiso
                  que se elige abajo NO decide la vuelta: el producto devuelto a otra estrategia
                  sigue el permiso de ésa (D2); la vuelta al precio de antes no tiene dueño vivo y
                  pide siempre (SUPERVISED); otra activa que ya lo alcanza manda (D5); un dueño
                  apagado o que no empezó recupera el producto pero el precio te espera (D7); con
                  el precio o el costo cambiados se resuelve a mano (D6). */}
              <Aviso tono="alerta" data-testid="aviso-vencimiento">
                {`El ${fecha(b.hastaCuando)} deja de correr y cada producto vuelve a quien lo tenía antes. Si lo tenía otra estrategia, vuelve a ella aunque esté apagada o todavía no haya empezado: si está andando, ésa decide su precio con su permiso; si está apagada o todavía no empezó, la vuelta a su precio de antes te espera en Por decidir. Si otra estrategia activa ya lo alcanza, sigue con ésa. Si no lo tenía ninguna, la vuelta a su precio de antes te espera en Por decidir. Un precio que alguien cambió a mano después no se toca, y si el precio o el costo cambian mientras la vuelta espera, no vuelve sola: la resolvés a mano desde la vuelta.`}
              </Aviso>
            </>
          )}
        </div>
      </div>

      <PermisoDeEstrategia
        value={b.permiso ?? PERMISO_INICIAL}
        onChange={(permiso) => onChange({ permiso })}
        enColumnas
      />

      <ReglasQueLaAlcanzan body={cuerpoDeSimulacion(b)} />

      {avisoOriginalActiva && (
        <p data-testid="original-activa" className="text-xs text-alerta">
          La original todavía tiene estos productos (aunque esté apagada): si la copia se queda con
          ellos, la original se queda sin ellos.
        </p>
      )}
      {solape.estado === 'ERROR' && (
        <EstadoError onReintentar={solape.reintentar}>
          {mensajeDeError(solape.error, 'No se pudo ver si se pisa con otra estrategia.')}
        </EstadoError>
      )}
      {solape.estado === 'LISTO' && solape.data.solapados > 0 && (
        <Aviso
          tono="alerta"
          data-testid="solapados"
          className="text-sm"
          titulo={`${formatoCantidad(solape.data.solapados)} ya ${solape.data.solapados === 1 ? 'tiene' : 'tienen'} otra estrategia: ${solape.data.porEstrategia
            .map((p, _i, todas) => {
              const quien = `${p.cuantos === 1 ? 'lo tiene' : 'los tiene'} «${p.applicationName}»`;
              // Con varias estrategias, cuántos tiene cada una (1oct-B12); con una sola ya lo dice el título.
              return todas.length > 1 ? `${formatoCantidad(p.cuantos)} ${quien}` : quien;
            })
            .join(' · ')}`}
        >
          <div className="space-y-2">
            <p>Un producto tiene una sola estrategia: si dos lo cubren, se pelean el precio.</p>
            {/* Lote A del 2-oct (A3): antes era una línea suelta arriba, sin «Ver cuáles». */}
            {b.onOverlap == null && (
              <p>
                {solape.data.solapados === 1
                  ? 'Mientras no elijas, «si hoy corriera» no lo cuenta.'
                  : 'Mientras no elijas, «si hoy corriera» no los cuenta.'}
              </p>
            )}
            {/* D2 (re-auditoría del 27-sep noche): con fecha de fin, lo que toma se
                devuelve al vencer (plan 2, P2-D7, `strategy_takeovers`). */}
            {b.hastaCuando != null && <p>Al vencer, se los devuelve a la que los tenía.</p>}
            {/* A2: «Ver cuáles» sólo informa; la decisión sigue siendo para todos juntos.
                Maqueta agregados.html#ya-tienen: «Ver cuáles» a la izquierda, los dos
                botones juntos a la derecha (un espaciador los empuja; no se estiran). */}
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {grupo && <VerCualesYaTienenOtra grupo={grupo} cuantos={solape.data.solapados} />}
              <div className="ml-auto flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={b.onOverlap === 'DEJARLOS'}
                  className={eleccion(b.onOverlap === 'DEJARLOS')}
                  onClick={() => onChange({ onOverlap: 'DEJARLOS' })}
                >
                  Dejarlos con la estrategia que ya tienen
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={b.onOverlap === 'TOMARLOS'}
                  className={eleccion(b.onOverlap === 'TOMARLOS')}
                  onClick={() => onChange({ onOverlap: 'TOMARLOS' })}
                >
                  Que los tome esta estrategia
                </Button>
              </div>
            </div>
          </div>
        </Aviso>
      )}

      <section aria-label="Resumen" data-testid="resumen" className="rounded-md border p-3 text-sm">
        <div className="font-medium">«{b.nombre.trim() || 'Estrategia sin nombre'}»</div>
        <p className="text-muted-foreground">{resumenDelBorrador(b)}</p>
      </section>
    </div>
  );
}
